import {
  loginIcon,
  RetryIcon,
  TrashIcon,
} from "@excalidraw/excalidraw/components/icons";
import { useCallback, useEffect, useState } from "react";

import { useAtomValue } from "../app-jotai";
import {
  adminApi,
  ApiError,
  type AdminOverview,
  type AdminUser,
  type UserRole,
} from "../data/api";
import { adminPanelOpenAtom } from "../data/authState";
import { BrandLogo } from "./BrandLogo";

import "./AdminPanel.scss";

type AdminPanelProps = {
  currentUserId: string;
  onLogout: () => void;
};

const formatDate = (date: string | null) =>
  date
    ? new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      }).format(new Date(date))
    : "Never";

const getErrorMessage = (error: unknown) =>
  error instanceof ApiError ? error.message : "Something went wrong";

export const AdminPanel = ({ currentUserId, onLogout }: AdminPanelProps) => {
  const isOpen = useAtomValue(adminPanelOpenAtom);
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async (search = "") => {
    setIsLoading(true);
    setError(null);
    try {
      const [nextOverview, nextUsers] = await Promise.all([
        adminApi.overview(),
        adminApi.users(search),
      ]);
      setOverview(nextOverview);
      setUsers(nextUsers);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadData();
      setQuery("");
      setConfirmDeleteId(null);
    }
  }, [isOpen, loadData]);

  if (!isOpen) {
    return null;
  }

  const updateUser = async (
    user: AdminUser,
    changes: { role?: UserRole; isDisabled?: boolean },
  ) => {
    setPendingUserId(user.id);
    setError(null);
    try {
      await adminApi.updateUser(user.id, changes);
      await loadData(query);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setPendingUserId(null);
    }
  };

  const deleteUser = async (user: AdminUser) => {
    setPendingUserId(user.id);
    setError(null);
    try {
      await adminApi.deleteUser(user.id);
      setConfirmDeleteId(null);
      await loadData(query);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setPendingUserId(null);
    }
  };

  const stats = overview
    ? [
        { label: "Users", value: overview.users },
        { label: "Projects", value: overview.projects },
        { label: "Screens", value: overview.screens },
        { label: "New this week", value: overview.newUsers },
        { label: "Suspended", value: overview.disabledUsers },
      ]
    : [];

  return (
    <div className="AdminPanel" role="dialog" aria-label="Admin panel">
      <header className="AdminPanel__topbar">
        <div className="AdminPanel__brand">
          <BrandLogo />
          <span>Administration</span>
        </div>
        <button
          type="button"
          className="AdminPanel__logoutButton"
          onClick={onLogout}
        >
          {loginIcon}
          Log out
        </button>
      </header>

      <main className="AdminPanel__scroll">
        <div className="AdminPanel__content">
          <div className="AdminPanel__heading">
            <div>
              <p className="AdminPanel__eyebrow">Workspace overview</p>
              <h1>Admin panel</h1>
            </div>
            <button
              type="button"
              className="AdminPanel__secondaryButton"
              onClick={() => loadData(query)}
              disabled={isLoading}
            >
              {RetryIcon}
              Refresh
            </button>
          </div>

          <section className="AdminPanel__stats" aria-label="Workspace stats">
            {stats.map((stat) => (
              <div className="AdminPanel__stat" key={stat.label}>
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </div>
            ))}
          </section>

          <section className="AdminPanel__users">
            <div className="AdminPanel__sectionHeader">
              <div>
                <h2>Users</h2>
                <span>Up to 100 accounts are shown</span>
              </div>
              <form
                className="AdminPanel__search"
                onSubmit={(event) => {
                  event.preventDefault();
                  loadData(query);
                }}
              >
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search email"
                  aria-label="Search users by email"
                />
                <button type="submit">Search</button>
              </form>
            </div>

            {error && <div className="AdminPanel__error">{error}</div>}
            {isLoading && users.length === 0 ? (
              <div className="AdminPanel__empty">Loading accounts...</div>
            ) : (
              <div className="AdminPanel__tableWrap">
                <table className="AdminPanel__table">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Content</th>
                      <th>Joined</th>
                      <th>Last login</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => {
                      const isSelf = user.id === currentUserId;
                      const isPending = pendingUserId === user.id;
                      return (
                        <tr key={user.id}>
                          <td>
                            <div className="AdminPanel__account">
                              <span className="AdminPanel__avatar">
                                {user.email.charAt(0).toUpperCase()}
                              </span>
                              <span>
                                <strong>{user.email}</strong>
                                {isSelf && <small>You</small>}
                              </span>
                            </div>
                          </td>
                          <td className="AdminPanel__muted">
                            {user._count.projects} projects,{" "}
                            {user._count.screens} screens
                          </td>
                          <td className="AdminPanel__muted">
                            {formatDate(user.createdAt)}
                          </td>
                          <td className="AdminPanel__muted">
                            {formatDate(user.lastLoginAt)}
                          </td>
                          <td>
                            <select
                              value={user.role}
                              disabled={isPending}
                              aria-label={`Role for ${user.email}`}
                              onChange={(event) =>
                                updateUser(user, {
                                  role: event.target.value as UserRole,
                                })
                              }
                            >
                              <option value="USER">User</option>
                              <option value="ADMIN">Admin</option>
                            </select>
                          </td>
                          <td>
                            <label className="AdminPanel__status">
                              <input
                                type="checkbox"
                                checked={!user.isDisabled}
                                disabled={isSelf || isPending}
                                onChange={(event) =>
                                  updateUser(user, {
                                    isDisabled: !event.target.checked,
                                  })
                                }
                              />
                              <span>
                                {user.isDisabled ? "Suspended" : "Active"}
                              </span>
                            </label>
                          </td>
                          <td className="AdminPanel__actions">
                            {confirmDeleteId === user.id ? (
                              <div className="AdminPanel__confirm">
                                <button
                                  type="button"
                                  className="AdminPanel__dangerButton"
                                  disabled={isPending}
                                  onClick={() => deleteUser(user)}
                                >
                                  Confirm
                                </button>
                                <button
                                  type="button"
                                  className="AdminPanel__textButton"
                                  onClick={() => setConfirmDeleteId(null)}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="AdminPanel__iconButton AdminPanel__iconButton--danger"
                                aria-label={`Delete ${user.email}`}
                                title="Delete user"
                                disabled={isSelf || isPending}
                                onClick={() => setConfirmDeleteId(user.id)}
                              >
                                {TrashIcon}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {!isLoading && users.length === 0 && (
                  <div className="AdminPanel__empty">No matching users</div>
                )}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};
